/**
 * AG Charts Theme Configuration for ForexJournal
 * Semantic color mapping for win/loss/neutral states
 * Designed for dark theme with professional prop-firm aesthetic
 */

// Register AG Charts Community modules (required for v13+)
import { ModuleRegistry, AllCommunityModule } from 'ag-charts-community';
ModuleRegistry.registerModules([AllCommunityModule]);

/**
 * Semantic color palette
 * @typedef {Object} ChartColors
 * @property {string} win - Color for winning trades
 * @property {string} loss - Color for losing trades
 * @property {string} breakeven - Color for breakeven trades
 * @property {string} neutral - Color for neutral/informational elements
 * @property {string} primary - Primary brand color (ctx-teal)
 * @property {string} secondary - Secondary accent color
 * @property {string} background - Chart background color
 * @property {string} text - Primary text color
 * @property {string} textMuted - Muted text color
 * @property {string} gridLine - Grid line color
 * @property {string} axis - Axis line color
 */

/**
 * Returns chart colors based on current theme.
 * @param {boolean} isDark
 * @returns {ChartColors}
 */
export const getChartColors = (isDark = true) => ({
  win: '#22c55e',
  loss: '#ef4444',
  breakeven: '#a1a1aa',
  neutral: '#71717a',
  primary: '#00D4C8',
  secondary: '#1A6FE8',
  background: 'transparent',
  text: isDark ? '#fafafa' : '#111827',
  textMuted: isDark ? '#a1a1aa' : '#6b7280',
  gridLine: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
  axis: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.15)',
});

/** @type {ChartColors} — dark-mode defaults kept for backward compatibility */
export const CHART_COLORS = getChartColors(true);

/**
 * Category colors for different groupings
 * Used in stacked/grouped bar charts
 */
export const CATEGORY_COLORS = {
  // Symbol colors (forex pairs)
  'EUR/USD': '#3b82f6',  // Blue
  'GBP/USD': '#8b5cf6',  // Violet
  'USD/JPY': '#f59e0b',  // Amber
  'USD/CHF': '#06b6d4',  // Cyan
  'AUD/USD': '#10b981',  // Emerald
  'USD/CAD': '#f97316',  // Orange
  'NZD/USD': '#84cc16',  // Lime
  'EUR/GBP': '#ec4899',  // Pink
  'EUR/JPY': '#6366f1',  // Indigo
  'GBP/JPY': '#14b8a6',  // Teal

  // Direction colors
  'BUY': '#22c55e',      // Green
  'SELL': '#ef4444',     // Red
  'LONG': '#22c55e',     // Green (alias)
  'SHORT': '#ef4444',    // Red (alias)

  // Asset type colors
  'forex': '#3b82f6',    // Blue
  'crypto': '#f59e0b',   // Amber
  'stocks': '#10b981',   // Emerald
  'indices': '#8b5cf6',  // Violet
  'commodities': '#f97316', // Orange

  // Order type colors
  'market': '#3b82f6',   // Blue
  'limit': '#8b5cf6',    // Violet
  'stop': '#f59e0b',     // Amber
  'stop-limit': '#06b6d4', // Cyan
};

/**
 * Get color for a category value
 * @param {string} category - The category value
 * @param {string} [fallback] - Fallback color if not found
 * @returns {string} - Hex color code
 */
export const getCategoryColor = (category, fallback = CHART_COLORS.neutral) => {
  return CATEGORY_COLORS[category] || CATEGORY_COLORS[category?.toUpperCase()] || fallback;
};

/**
 * Get result color based on profit value
 * @param {number} profit - The profit value
 * @param {number} [threshold=0] - Breakeven threshold
 * @returns {string} - Hex color code
 */
export const getResultColor = (profit, threshold = 0) => {
  if (profit > threshold) return CHART_COLORS.win;
  if (profit < -threshold) return CHART_COLORS.loss;
  return CHART_COLORS.breakeven;
};

/**
 * AG Charts theme configuration
 * Applied to all charts for consistent styling
 * Compatible with AG Charts v13
 */
export const AG_CHART_THEME = {
  baseTheme: 'ag-default-dark',
  palette: {
    fills: [
      CHART_COLORS.win,
      CHART_COLORS.loss,
      CHART_COLORS.breakeven,
      CHART_COLORS.primary,
      CHART_COLORS.secondary,
      '#3b82f6',
      '#8b5cf6',
      '#f59e0b',
    ],
    strokes: [
      CHART_COLORS.win,
      CHART_COLORS.loss,
      CHART_COLORS.breakeven,
      CHART_COLORS.primary,
      CHART_COLORS.secondary,
      '#3b82f6',
      '#8b5cf6',
      '#f59e0b',
    ],
  },
  overrides: {
    common: {
      background: {
        fill: CHART_COLORS.background,
      },
      title: {
        color: CHART_COLORS.text,
        fontWeight: 600,
        fontSize: 16,
      },
      subtitle: {
        color: CHART_COLORS.textMuted,
        fontSize: 12,
      },
      legend: {
        item: {
          label: {
            color: CHART_COLORS.text,
            fontSize: 12,
          },
          marker: {
            strokeWidth: 0,
          },
        },
        spacing: 16,
      },
    },
    bar: {
      axes: {
        category: {
          label: {
            color: CHART_COLORS.textMuted,
            fontSize: 11,
          },
          line: {
            stroke: CHART_COLORS.axis,
          },
          tick: {
            stroke: CHART_COLORS.axis,
          },
          gridLine: {
            style: [{
              stroke: CHART_COLORS.gridLine,
              lineDash: [4, 2],
            }],
          },
        },
        number: {
          label: {
            color: CHART_COLORS.textMuted,
            fontSize: 11,
          },
          line: {
            stroke: CHART_COLORS.axis,
          },
          tick: {
            stroke: CHART_COLORS.axis,
          },
          gridLine: {
            style: [{
              stroke: CHART_COLORS.gridLine,
              lineDash: [4, 2],
            }],
          },
        },
      },
      series: {
        bar: {
          label: {
            color: CHART_COLORS.text,
            fontSize: 10,
          },
        },
      },
    },
    line: {
      axes: {
        category: {
          label: {
            color: CHART_COLORS.textMuted,
            fontSize: 11,
          },
          line: {
            stroke: CHART_COLORS.axis,
          },
          tick: {
            stroke: CHART_COLORS.axis,
          },
          gridLine: {
            style: [{
              stroke: CHART_COLORS.gridLine,
              lineDash: [4, 2],
            }],
          },
        },
        number: {
          label: {
            color: CHART_COLORS.textMuted,
            fontSize: 11,
          },
          line: {
            stroke: CHART_COLORS.axis,
          },
          tick: {
            stroke: CHART_COLORS.axis,
          },
          gridLine: {
            style: [{
              stroke: CHART_COLORS.gridLine,
              lineDash: [4, 2],
            }],
          },
        },
        time: {
          label: {
            color: CHART_COLORS.textMuted,
            fontSize: 11,
          },
          line: {
            stroke: CHART_COLORS.axis,
          },
          tick: {
            stroke: CHART_COLORS.axis,
          },
          gridLine: {
            style: [{
              stroke: CHART_COLORS.gridLine,
              lineDash: [4, 2],
            }],
          },
        },
      },
      series: {
        line: {
          marker: {
            fill: CHART_COLORS.primary,
            stroke: CHART_COLORS.primary,
            size: 6,
          },
          strokeWidth: 2,
        },
      },
    },
    pie: {
      series: {
        pie: {
          calloutLabel: {
            color: CHART_COLORS.text,
            fontSize: 11,
          },
          sectorLabel: {
            color: CHART_COLORS.text,
            fontSize: 12,
            fontWeight: 600,
          },
        },
      },
    },
    scatter: {
      series: {
        scatter: {
          marker: {
            size: 8,
            strokeWidth: 1,
          },
        },
      },
    },
  },
};

/**
 * Returns a full AG Charts theme for the given mode.
 * Use this instead of the static AG_CHART_THEME export when inside a component.
 * @param {boolean} isDark
 */
export const getAgChartTheme = (isDark = true) => {
  const colors = getChartColors(isDark);
  const PALETTE_FILLS = [colors.win, colors.loss, colors.breakeven, colors.primary, colors.secondary, '#3b82f6', '#8b5cf6', '#f59e0b'];
  const axisConfig = {
    label: { color: colors.textMuted, fontSize: 11 },
    line: { stroke: colors.axis },
    tick: { stroke: colors.axis },
    gridLine: { style: [{ stroke: colors.gridLine, lineDash: [4, 2] }] },
  };
  return {
    baseTheme: isDark ? 'ag-default-dark' : 'ag-default',
    palette: { fills: PALETTE_FILLS, strokes: PALETTE_FILLS },
    overrides: {
      common: {
        background: { fill: colors.background },
        title: { color: colors.text, fontWeight: 600, fontSize: 16 },
        subtitle: { color: colors.textMuted, fontSize: 12 },
        legend: {
          item: { label: { color: colors.text, fontSize: 12 }, marker: { strokeWidth: 0 } },
          spacing: 16,
        },
      },
      bar: {
        axes: { category: axisConfig, number: axisConfig },
        series: { bar: { label: { color: colors.text, fontSize: 10 } } },
      },
      line: {
        axes: { category: axisConfig, number: axisConfig, time: axisConfig },
        series: { line: { marker: { fill: colors.primary, stroke: colors.primary, size: 6 }, strokeWidth: 2 } },
      },
      pie: {
        series: { pie: { calloutLabel: { color: colors.text, fontSize: 11 }, sectorLabel: { color: colors.text, fontSize: 12, fontWeight: 600 } } },
      },
      scatter: { series: { scatter: { marker: { size: 8, strokeWidth: 1 } } } },
    },
  };
};

/**
 * Common tooltip styling
 * @returns {Object} - Tooltip configuration
 */
export const getTooltipStyles = () => ({
  class: 'ag-chart-tooltip-custom',
});

/**
 * Format number with commas and decimal places
 * @param {number} value - Number to format
 * @param {number} [decimals=2] - Decimal places
 * @returns {string} - Formatted number
 */
export const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Format percentage value
 * @param {number} value - Value between 0-100
 * @param {number} [decimals=1] - Decimal places
 * @returns {string} - Formatted percentage
 */
export const formatPercent = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return '—';
  return `${value.toFixed(decimals)}%`;
};

/**
 * Format currency value
 * @param {number} value - Currency value
 * @param {string} [currency='USD'] - Currency code
 * @returns {string} - Formatted currency
 */
export const formatCurrency = (value, currency = 'USD') => {
  if (value === null || value === undefined || isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};
